import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';

import localUtil from '../../lib/util';

@observer
class Profile extends Component {
    formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            const date = new Date(dateStr);
            return util.formatDate(date, {
                locale: 'nb-NO',
                hour12: false,
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            });
        } catch (e) {
            return '-';
        }
    }

    getRatingChangeClass = (change) => {
        if (change > 0) return 'text-success';
        if (change < 0) return 'text-danger';
        return 'text-body-secondary';
    }

    getRatingChangeIcon = (change) => {
        if (change > 0) return <i class="fa-solid fa-arrow-up"></i>;
        if (change < 0) return <i class="fa-solid fa-arrow-down"></i>;
        return <i class="fa-solid fa-minus"></i>;
    }

    render() {
        const { musher, darkmode, drawerLevel } = this.props;
        const inDrawer = typeof drawerLevel !== 'undefined';

        if (!musher) {
            return (
                <div class="text-center py-5">
                    <i class="fa-solid fa-user-slash fa-3x mb-3 text-body-secondary"></i>
                    <p class="text-body-secondary"><Text id="musherRating.profileNotFound">Profile not found</Text></p>
                </div>
            );
        }

        const name = `${musher.firstname || ''} ${musher.lastname || ''}`.trim() || 'Unknown';
        const ratingHistory = (musher.ratingHistory || []).slice().reverse(); // Most recent first
        const yearlyRatings = (musher.yearlyRatings || []).slice().sort((a, b) => b.year - a.year);

        // Get image from userImages array
        const image = localUtil.getImage({ user: { images: musher.userImages }, size: 's3MediumLink', lastImage: true });

        // Calculate rank from leaderboard if available
        const { musherRatingStore } = this.props.stores || {};
        const leaderboard = musherRatingStore?.leaderboard || [];
        const rankEntry = leaderboard.find(m => m.id === musher.id);
        const rank = rankEntry ? leaderboard.indexOf(rankEntry) + 1 : null;

        return (
            <div class={`${inDrawer ? 'px-3 pb-3' : ''}`} style='overflow-x: hidden;'>
                <div class='row g-0'>
                {/* Profile Header */}
                {inDrawer && (
                    <div class="col-12 mb-4">
                        <div class='d-flex flex-row justify-content-center align-items-top'>
                            <div class={`d-flex flex-column mt-0 px-3 py-2 justify-content-center align-items-center text-overflow position-relative w-100`}>
                                <div
                                    class={`text-center rounded-circle imageRounded imageRoundedLarge`}
                                    style={`${image ? `background-image: url('${image}'); background-size: cover;` : ''} border: 2px solid #ffffff;`}
                                >
                                    {!image && <i class='fas fa-user text-body-secondary mt-5' style='font-size: 50px;' />}
                                </div>
                                {rank && (
                                    <div class='position-absolute' style='top: 110px; left: calc(50% - 110px);'>
                                        <span class='badge text-bg-dark rounded-pill me-1 ms-1' style='font-size: 2.0em; border: 2px solid #ffffff;'>{rank}</span>
                                    </div>
                                )}
                                {musher.country && (
                                    <div class='position-absolute' style='top: 110px; left: calc(50% + 40px);'>
                                        {localUtil.getFlagSvg(musher.country, 45, false, 'rounded-pill', 'border: 2px solid #ffffff;')}
                                    </div>
                                )}
                                <div class='d-flex flex-row mt-2 text-center' style='font-size: 1.5rem; word-break: break-word;'>
                                    <span>
                                        {musher.firstname} {musher.lastname} {musher.birth ? <span class='font-weight-lighter'>({util.age(musher.birth)})</span> : ''}
                                    </span>
                                </div>
                                <div class='d-flex flex-row' style='font-size: 0.9rem;'>
                                    <span class='ms-2 text-body-secondary'>
                                        <Text id="musherRating.rating">Rating</Text>: {musher.rating || 1200}
                                    </span>
                                </div>
                                {musher.email && (
                                    <div class='d-flex flex-row mt-2' style='font-size: 0.85rem; word-break: break-all;'>
                                        <span class='text-body-secondary'>
                                            <i class='fa-solid fa-envelope me-1' /> {musher.email}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Stats Cards */}
                <div class="col-12 mb-4">
                    <div class="card">
                        <div class="card-header">
                            <h6 class="mb-0"><Text id="musherRating.allTimeStatistics">All-Time Statistics</Text></h6>
                        </div>
                        <div class="card-body">
                            <div class="row text-center">
                                <div class="col-6 col-md-3 mb-3 mb-md-0">
                                    <div class="h2 mb-0">{musher.rating || 1200}</div>
                                    <small class="text-body-secondary"><Text id="musherRating.currentRating">Current Rating</Text></small>
                                </div>
                                <div class="col-6 col-md-3 mb-3 mb-md-0">
                                    <div class="h2 mb-0">{musher.totalRaces || 0}</div>
                                    <small class="text-body-secondary"><Text id="musherRating.totalRaces">Total Races</Text></small>
                                </div>
                                <div class="col-6 col-md-3">
                                    <div class="h2 mb-0">{musher.totalWins || 0}</div>
                                    <small class="text-body-secondary"><Text id="musherRating.wins">Wins</Text></small>
                                </div>
                                <div class="col-6 col-md-3">
                                    <div class="h2 mb-0">{musher.totalPodiums || 0}</div>
                                    <small class="text-body-secondary"><Text id="musherRating.podiums">Podiums</Text></small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Additional Stats */}
                <div class="col-12 col-md-6 mb-4">
                    <div class="card">
                        <div class="card-header">
                            <h5 class="mb-0"><Text id="musherRating.statistics">Statistics</Text></h5>
                        </div>
                        <div class="card-body">
                            <table class="table table-sm mb-0">
                                <tbody>
                                    <tr>
                                        <td><Text id="musherRating.bestFinish">Best Finish</Text></td>
                                        <td class="text-right">
                                            {musher.bestFinish ? (
                                                <span>
                                                    #{musher.bestFinish}
                                                    {musher.bestFinish <= 3 && (
                                                        <span class="ms-1 text-warning"><i class="fa-solid fa-trophy"></i></span>
                                                    )}
                                                </span>
                                            ) : '-'}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td><Text id="musherRating.averageFinish">Average Finish</Text></td>
                                        <td class="text-right">{musher.averageFinish ? `#${musher.averageFinish.toFixed(1)}` : '-'}</td>
                                    </tr>
                                    <tr>
                                        <td><Text id="musherRating.lastRace">Last Race</Text></td>
                                        <td class="text-right">{this.formatDate(musher.lastRaceDate)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Yearly Ratings */}
                {yearlyRatings.length > 0 && (
                    <div class="col-12 col-md-6 mb-4">
                        <div class="card">
                            <div class="card-header">
                                <h5 class="mb-0"><Text id="musherRating.yearlyRatings">Yearly Statistics</Text></h5>
                            </div>
                            <div class="card-body">
                                <table class="table table-sm mb-0">
                                    <thead>
                                        <tr>
                                            <th><Text id="musherRating.year">Year</Text></th>
                                            <th class="text-center"><Text id="musherRating.rating">Rating</Text></th>
                                            <th class="text-center"><Text id="musherRating.rank">Rank</Text></th>
                                            <th class="text-center"><Text id="musherRating.races">Races</Text></th>
                                            <th class="text-center"><Text id="musherRating.wins">Wins</Text></th>
                                            <th class="text-center"><Text id="musherRating.podiums">Podiums</Text></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {yearlyRatings.map(yr => (
                                            <tr key={yr.year}>
                                                <td>{yr.year}</td>
                                                <td class="text-center">{yr.rating}</td>
                                                <td class="text-center">#{yr.rank}</td>
                                                <td class="text-center">{yr.racesCompleted}</td>
                                                <td class="text-center">{yr.wins || 0}</td>
                                                <td class="text-center">{yr.podiums || 0}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Race History */}
                <div class="col-12">
                    <div class="card">
                        <div class="card-header">
                            <h5 class="mb-0"><Text id="musherRating.raceHistory">Race History</Text></h5>
                        </div>
                        <div class="card-body p-0">
                            {ratingHistory.length === 0 ? (
                                <div class="p-4 text-center text-body-secondary">
                                    <Text id="musherRating.noRaceHistory">No race history available</Text>
                                </div>
                            ) : (
                                <div class="table-responsive">
                                    <table class="table table-sm mb-0">
                                        <thead>
                                            <tr>
                                                <th><Text id="musherRating.date">Date</Text></th>
                                                <th><Text id="musherRating.race">Race</Text></th>
                                                <th class="text-center"><Text id="musherRating.position">Pos</Text></th>
                                                <th class="text-center"><Text id="musherRating.change">Change</Text></th>
                                                <th class="text-center"><Text id="musherRating.rating">Rating</Text></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {ratingHistory.map((entry, idx) => (
                                                <tr key={idx}>
                                                    <td class="small">{this.formatDate(entry.date)}</td>
                                                    <td>
                                                        <div>{entry.raceTitle || 'Unknown Race'}</div>
                                                        {entry.raceClassTitle && (
                                                            <small class="text-body-secondary">{entry.raceClassTitle}</small>
                                                        )}
                                                        {entry.isExternal && (
                                                            <span class="badge bg-info ms-1" title="External Race">Ext</span>
                                                        )}
                                                    </td>
                                                    <td class="text-center">
                                                        <span>
                                                            {entry.position}/{entry.totalContestants}
                                                        </span>
                                                        {entry.position <= 3 && (
                                                            <span class="ms-1 text-warning"><i class="fa-solid fa-trophy fa-sm"></i></span>
                                                        )}
                                                    </td>
                                                    <td class={`text-center ${this.getRatingChangeClass(entry.ratingChange)}`}>
                                                        {this.getRatingChangeIcon(entry.ratingChange)}
                                                        <span class="ms-1">
                                                            {entry.ratingChange > 0 ? '+' : ''}{entry.ratingChange}
                                                        </span>
                                                    </td>
                                                    <td class="text-center">
                                                        <strong>{entry.ratingAfter}</strong>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                </div>
            </div>
        );
    }
}

export default Profile;
